import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import {
  adminApi,
  ApiError,
  type GroupEmail,
  type GroupEmailFields,
  type GroupEmailKind,
  type GroupEmailOptions,
  type GroupEmailTemplate,
  type RecipientCount,
} from '../lib/api.js';
import { useAsync } from '../lib/useAsync.js';
import { useCan } from '../lib/capabilities.js';
import {
  groupEmailStatusLabel,
  isNotFound,
  nextAt,
  outcomeSummary,
  parseAddressList,
  scheduleLabel,
  toLocalInput,
  type NotificationSection,
} from '../lib/notifications.js';
import { TabNav, type RecordTabs } from '../components/Record.js';
import { FilterPills, InfoBubble, PrimaryButton, Screen, inputStyle } from '../components/Screen.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { AsyncBoundary, Badge, Button, EmptyState, HoldButton, Note, Panel, Table, Td, When } from '../components/ui.js';

/**
 * Group email: staff writing to security groups or typed addresses, in the
 * Ridgewill or Keyodo template (RidgeWill/auth routes/admin-group-email.ts).
 *
 * ── The preview is the real email ────────────────────────────────────
 *
 * auth renders it, with the same code that renders every copy, so what this
 * screen shows is what arrives. The portal's CSP forbids inline style
 * attributes, and an email is nothing but inline styles, so the preview moves
 * each one onto its element through the CSSOM (`el.style.cssText`), which the
 * policy allows, inside a shadow root so the portal's own CSS cannot reach it.
 *
 * ── Sending is held, not clicked ─────────────────────────────────────
 *
 * Send now opens a summary of who it reaches, and the send itself is a hold
 * (Jon, 2026-11-01): an email to a list cannot be recalled, and a hold cannot
 * be produced by a reflex.
 */

type Tabs = RecordTabs<NotificationSection>;
const BASE = '/notifications/group-email';

function GroupTabs({ tabs }: { tabs: Tabs }) {
  return <TabNav base="/notifications" tabs={tabs} current="group-email" label="Notifications sections" />;
}

const message = (err: unknown) => (err instanceof Error ? err.message : String(err));

// ── The list ─────────────────────────────────────────────────────────

type Filter = 'all' | 'draft' | 'scheduled' | 'sent';

export function GroupEmailList({ tabs, navigate }: { tabs: Tabs; navigate: (to: string) => void }) {
  const canSend = useCan('notification:send');
  const [filter, setFilter] = useState<Filter>('all');
  const { state, reload } = useAsync(() => adminApi.groupEmails(), []);

  const counts = state.status === 'ready' ? countBy(state.data.items) : null;
  const label = (f: Filter, word: string) => (counts && f !== 'all' && counts[f] ? `${word} ${counts[f]}` : word);

  return (
    <Screen
      filters={
        <FilterPills<Filter>
          label="Show"
          current={filter}
          onSelect={setFilter}
          options={[
            { value: 'all', label: 'All' },
            { value: 'draft', label: label('draft', 'Drafts') },
            { value: 'scheduled', label: label('scheduled', 'Scheduled') },
            { value: 'sent', label: 'Sent' },
          ]}
        />
      }
      actions={
        <>
          <Button onClick={() => reload()}>Refresh</Button>
          {canSend ? <PrimaryButton onClick={() => navigate(`${BASE}/new`)}>New email</PrimaryButton> : null}
        </>
      }
    >
      <GroupTabs tabs={tabs} />
      <Panel
        title="Group emails"
        info={
          <InfoBubble label="Where the copies go">
            Each person gets their own copy, logged on the Sends tab as group-announcement or group-notice, with its
            delivery result.
          </InfoBubble>
        }
      >
        {state.status === 'failed' ? (
          isNotFound(state.message) ? (
            <Note>Group email could not be read. Either your role does not allow it, or the auth service is older than group email.</Note>
          ) : (
            <Note tone="danger">{state.message}</Note>
          )
        ) : (
          <AsyncBoundary state={state}>
            {(d) => {
              const rows = d.items.filter((e) => filter === 'all' || e.status === filter || (filter === 'sent' && e.status === 'sending'));
              if (rows.length === 0) {
                return (
                  <EmptyState
                    title={filter === 'all' ? 'No group emails yet' : `No ${filter === 'draft' ? 'drafts' : filter} emails`}
                    suggestion={canSend ? 'New email starts one. It stays a draft until you send or schedule it.' : undefined}
                  />
                );
              }
              return (
                <Table columns={['Subject', 'Template', 'To', 'Status', 'Last change']}>
                  {rows.map((e) => (
                    <tr
                      key={e.id}
                      className="rw-rowhover"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`${BASE}/${encodeURIComponent(e.id)}`)}
                    >
                      <Td>
                        <a href={`#${BASE}/${encodeURIComponent(e.id)}`} style={s.subjectLink} onClick={(ev) => ev.stopPropagation()}>
                          {e.subject || <span style={{ color: C.muted }}>No subject</span>}
                        </a>
                        <div style={s.sub}>{e.kind === 'notice' ? 'Notice' : 'Announcement'}</div>
                      </Td>
                      <Td>
                        <TemplateMark template={e.template as GroupEmailTemplate} />
                      </Td>
                      <Td>{audience(e)}</Td>
                      <Td>
                        <StatusBadge status={e.status} />
                        <div style={s.sub}>{statusDetail(e)}</div>
                      </Td>
                      <Td>
                        <span style={{ whiteSpace: 'nowrap' }}>
                          {e.updatedBy ?? 'Someone'}, <When iso={e.updatedAt} />
                        </span>
                      </Td>
                    </tr>
                  ))}
                </Table>
              );
            }}
          </AsyncBoundary>
        )}
      </Panel>
    </Screen>
  );
}

function countBy(items: GroupEmail[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const e of items) out[e.status] = (out[e.status] ?? 0) + 1;
  return out;
}

function audience(e: Pick<GroupEmail, 'groups' | 'addresses'>): string {
  const names = e.groups.map((g) => g.name ?? 'a deleted group');
  const typed = e.addresses.length ? `${e.addresses.length} address${e.addresses.length === 1 ? '' : 'es'}` : '';
  if (names.length && typed) return `${names.join(', ')} + ${typed}`;
  return names.join(', ') || typed || 'Nobody yet';
}

function statusDetail(e: GroupEmail): string {
  if (e.status === 'scheduled' && e.scheduledAt) return scheduleLabel(new Date(e.scheduledAt));
  if ((e.status === 'sent' || e.status === 'sending') && e.outcomes) return outcomeSummary(e.outcomes);
  return '';
}

function StatusBadge({ status }: { status: string }) {
  const tone = status === 'sent' ? 'good' : status === 'scheduled' || status === 'sending' ? 'warn' : 'neutral';
  return <Badge tone={tone}>{groupEmailStatusLabel(status)}</Badge>;
}

function TemplateMark({ template }: { template: GroupEmailTemplate }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem', whiteSpace: 'nowrap' }}>
      <span
        aria-hidden="true"
        style={{
          width: 10,
          height: 10,
          borderRadius: 3,
          background: template === 'keyodo' ? '#262218' : C.brand,
          // Keyodo's ink is close to the dark theme's ground; the rule keeps it visible.
          boxShadow: template === 'keyodo' ? `inset 0 -3px 0 #CB4A2E, 0 0 0 1px ${C.inputBorder}` : undefined,
        }}
      />
      {template === 'keyodo' ? 'Keyodo' : 'Ridgewill'}
    </span>
  );
}

// ── The composer ─────────────────────────────────────────────────────

const EMPTY: GroupEmailFields = {
  template: 'ridgewill',
  kind: 'announcement',
  subject: '',
  preheader: '',
  bodyHtml: '',
  groupIds: [],
  addresses: [],
};

export function GroupEmailCompose({ tabs, id, navigate }: { tabs: Tabs; id: string; navigate: (to: string) => void }) {
  const existing = useAsync(() => (id === 'new' ? Promise.resolve(null) : adminApi.groupEmail(id)), [id]);
  const options = useAsync(() => adminApi.groupEmailOptions(), []);

  return (
    <Screen>
      <GroupTabs tabs={tabs} />
      <a href={`#${BASE}`} style={s.back}>
        &larr; Group email
      </a>
      {existing.state.status === 'failed' ? (
        <Note tone="danger">{isNotFound(existing.state.message) ? 'This email could not be found.' : existing.state.message}</Note>
      ) : (
        <AsyncBoundary state={existing.state}>
          {(email) => <Composer key={email?.id ?? 'new'} initial={email} options={options.state.status === 'ready' ? options.state.data : null} navigate={navigate} />}
        </AsyncBoundary>
      )}
    </Screen>
  );
}

function fieldsOf(e: GroupEmail): GroupEmailFields {
  return {
    template: e.template,
    kind: e.kind,
    subject: e.subject,
    preheader: e.preheader,
    bodyHtml: e.bodyHtml,
    groupIds: e.groups.map((g) => g.id),
    addresses: e.addresses,
  };
}

const AUTOSAVE_MS = 2500;

function Composer({
  initial,
  options,
  navigate,
}: {
  initial: GroupEmail | null;
  options: GroupEmailOptions | null;
  navigate: (to: string) => void;
}) {
  const canSend = useCan('notification:send');
  const [email, setEmail] = useState<GroupEmail | null>(initial);
  const [fields, setFields] = useState<GroupEmailFields>(initial ? fieldsOf(initial) : EMPTY);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(initial ? new Date(initial.updatedAt) : null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<RecipientCount | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const locked = !canSend || (email !== null && email.status !== 'draft' && email.status !== 'scheduled');
  const update = (patch: Partial<GroupEmailFields>) => {
    setFields((f) => ({ ...f, ...patch }));
    setDirty(true);
  };

  /** Save what is on screen: create the draft the first time, change it after. */
  const save = useCallback(async (): Promise<GroupEmail> => {
    setSaving(true);
    setError(null);
    try {
      const saved = email ? await adminApi.updateGroupEmail(email.id, fields) : await adminApi.createGroupEmail(fields);
      if (!email) {
        // The address bar follows the new draft without remounting the screen.
        window.history.replaceState(null, '', `#${BASE}/${encodeURIComponent(saved.id)}`);
      }
      setEmail(saved);
      setDirty(false);
      setSavedAt(new Date());
      return saved;
    } finally {
      setSaving(false);
    }
  }, [email, fields]);

  // Autosave a draft a moment after the last change, once there is anything to keep.
  const hasContent = Boolean(fields.subject.trim() || fields.bodyHtml.replace(/<[^>]+>|&nbsp;|\s/g, ''));
  useEffect(() => {
    if (!dirty || locked || saving || !hasContent || email?.status === 'scheduled') return;
    const t = setTimeout(() => void save().catch((err) => setError(message(err))), AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [dirty, locked, saving, hasContent, email?.status, save]);

  const run = async (label: string, action: () => Promise<void>) => {
    setBusy(label);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const openSend = () =>
    run('send', async () => {
      await save();
      setConfirming(await adminApi.groupEmailRecipients({ groupIds: fields.groupIds, addresses: fields.addresses }));
    });

  const sendNow = () =>
    run('send', async () => {
      const id = email?.id;
      if (!id) return;
      const sent = await adminApi.sendGroupEmail(id);
      setConfirming(null);
      setEmail(sent);
      setNotice(
        `Sent. ${sent.queued} ${sent.queued === 1 ? 'copy is' : 'copies are'} on the way${sent.notQueued ? `, ${sent.notQueued} could not be queued` : ''}.`,
      );
    });

  const schedule = (at: Date) =>
    run('schedule', async () => {
      const saved = await save();
      setEmail(await adminApi.scheduleGroupEmail(saved.id, at.toISOString()));
      setNotice(`Scheduled for ${scheduleLabel(at)}.`);
    });

  const unschedule = () =>
    run('unschedule', async () => {
      if (!email) return;
      setEmail(await adminApi.unscheduleGroupEmail(email.id));
      setNotice('Back to a draft. Nothing will be sent until you send or schedule it again.');
    });

  const sendTest = () =>
    run('test', async () => {
      const saved = await save();
      const { to } = await adminApi.testGroupEmail(saved.id);
      setNotice(`A test copy is on its way to ${to}.`);
    });

  const remove = () =>
    run('delete', async () => {
      if (email) await adminApi.deleteGroupEmail(email.id);
      navigate(BASE);
    });

  const copyToDraft = () =>
    run('copy', async () => {
      const copy = await adminApi.createGroupEmail({ ...fields, subject: fields.subject });
      navigate(`${BASE}/${encodeURIComponent(copy.id)}`);
    });

  const status = email?.status ?? 'draft';

  return (
    <>
      {error ? <Note tone="danger">{error}</Note> : null}
      {notice ? <Note>{notice}</Note> : null}
      {!canSend ? <Note>Your role can read group email but not write or send it. That needs the notification send permission.</Note> : null}
      {email?.status === 'scheduled' && email.scheduledAt ? (
        <Note tone="warn">
          Scheduled for {scheduleLabel(new Date(email.scheduledAt))}. Changes save as you make them and go out with it.{' '}
          {canSend ? (
            <button type="button" style={s.inlineLink} onClick={unschedule} disabled={busy !== null}>
              Unschedule
            </button>
          ) : null}
        </Note>
      ) : null}

      <div className="rw-compose">
        <section style={s.panel} aria-label="Email">
          <header style={s.panelHead}>
            <h2 style={s.panelTitle}>{fields.subject || 'New email'}</h2>
            <StatusBadge status={status} />
          </header>

          <Row label="Template">
            <Segmented<GroupEmailTemplate>
              label="Template"
              value={fields.template}
              disabled={locked}
              onChange={(template) => update({ template })}
              options={[
                { value: 'ridgewill', label: <TemplateMark template="ridgewill" /> },
                { value: 'keyodo', label: <TemplateMark template="keyodo" /> },
              ]}
            />
          </Row>

          <Row
            label="Kind"
            hint={
              fields.kind === 'announcement'
                ? 'Promotional. Carries an unsubscribe link and skips anyone who opted out.'
                : 'For service, security or policy notices only. Reaches everyone, opted out or not, and has no unsubscribe link.'
            }
          >
            <Segmented<GroupEmailKind>
              label="Kind"
              value={fields.kind}
              disabled={locked}
              onChange={(kind) => update({ kind })}
              options={[
                { value: 'announcement', label: 'Announcement' },
                { value: 'notice', label: 'Notice' },
              ]}
            />
          </Row>

          <Row label="To" htmlFor="ge-to">
            <Recipients fields={fields} options={options} disabled={locked} onChange={update} />
          </Row>

          <Row label="Subject" htmlFor="ge-subject">
            <input
              id="ge-subject"
              type="text"
              value={fields.subject}
              disabled={locked}
              maxLength={200}
              onChange={(e) => update({ subject: e.target.value })}
              placeholder="What the email is about"
              style={s.input}
            />
          </Row>

          <Row
            label="Preview text"
            htmlFor="ge-pre"
            hint="The grey line an inbox shows after the subject. Left empty, inboxes show the first words of the email."
          >
            <input
              id="ge-pre"
              type="text"
              value={fields.preheader}
              disabled={locked}
              maxLength={200}
              onChange={(e) => update({ preheader: e.target.value })}
              style={s.input}
            />
          </Row>

          <Row label="Message">
            <RichEditor
              initialHtml={fields.bodyHtml}
              disabled={locked}
              mergeFields={options?.mergeFields ?? []}
              onChange={(bodyHtml) => update({ bodyHtml })}
            />
          </Row>

          <footer style={s.actions}>
            <span style={s.saved} aria-live="polite">
              {saving ? 'Saving…' : dirty ? 'Unsaved changes' : savedAt ? <>Saved <When iso={savedAt.toISOString()} /></> : 'Not saved yet'}
            </span>
            {locked ? (
              canSend ? (
                <Button onClick={copyToDraft} disabled={busy !== null}>
                  Copy to a new draft
                </Button>
              ) : null
            ) : (
              <>
                {email?.status === 'draft' ? (
                  <HoldButton onHold={remove} disabled={busy !== null} title="Hold to delete this draft">
                    Delete draft
                  </HoldButton>
                ) : null}
                <Button onClick={sendTest} disabled={busy !== null || saving}>
                  {busy === 'test' ? 'Sending test…' : 'Send a test to me'}
                </Button>
                <Button onClick={() => void run('save', async () => void (await save()))} disabled={busy !== null || saving}>
                  Save draft
                </Button>
                <ScheduleMenu disabled={busy !== null || saving} onSchedule={schedule} rescheduling={email?.status === 'scheduled'} />
                <PrimaryButton onClick={openSend} disabled={busy !== null || saving}>
                  Send now
                </PrimaryButton>
              </>
            )}
          </footer>
        </section>

        <div className="rw-compose-preview">
          {email?.outcomes ? <Outcomes email={email} /> : null}
          <Preview fields={fields} />
        </div>
      </div>

      {confirming ? (
        <ConfirmSend
          count={confirming}
          fields={fields}
          busy={busy === 'send'}
          onCancel={() => setConfirming(null)}
          onSend={sendNow}
        />
      ) : null}
    </>
  );
}

function Row({ label, hint, htmlFor, children }: { label: string; hint?: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="rw-compose-row">
      {htmlFor ? (
        <label htmlFor={htmlFor} style={s.rowLabel}>
          {label}
        </label>
      ) : (
        <span style={s.rowLabel}>{label}</span>
      )}
      <div style={{ minWidth: 0 }}>
        {children}
        {hint ? <div style={s.hint}>{hint}</div> : null}
      </div>
    </div>
  );
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={label} style={s.segment}>
      {options.map((o, i) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            style={{
              ...s.segmentButton,
              ...(i > 0 ? { borderLeft: `1px solid ${C.inputBorder}` } : {}),
              ...(on ? { background: C.blueTint, color: C.blueText, fontWeight: 640 } : {}),
              cursor: disabled ? 'default' : 'pointer',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Recipients ───────────────────────────────────────────────────────

function Recipients({
  fields,
  options,
  disabled,
  onChange,
}: {
  fields: GroupEmailFields;
  options: GroupEmailOptions | null;
  disabled: boolean;
  onChange: (patch: Partial<GroupEmailFields>) => void;
}) {
  const [draft, setDraft] = useState('');
  const [picking, setPicking] = useState(false);
  const [count, setCount] = useState<RecipientCount | null>(null);
  const [countError, setCountError] = useState<string | null>(null);

  // Who it reaches, a moment after the list stops changing.
  const key = JSON.stringify([fields.groupIds, fields.addresses]);
  useEffect(() => {
    if (fields.groupIds.length === 0 && fields.addresses.length === 0) {
      setCount(null);
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      adminApi
        .groupEmailRecipients({ groupIds: fields.groupIds, addresses: fields.addresses })
        .then((c) => live && (setCount(c), setCountError(null)))
        .catch((err) => live && setCountError(message(err)));
    }, 400);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [key]);

  const commit = (text: string) => {
    const added = parseAddressList(text);
    if (added.length) {
      const have = new Set(fields.addresses.map((a) => a.toLowerCase()));
      onChange({ addresses: [...fields.addresses, ...added.filter((a) => !have.has(a.toLowerCase()))] });
    }
    setDraft('');
  };

  const groupName = (id: string) => options?.items.find((g) => g.id === id)?.name ?? 'Group';
  const groupMembers = (id: string) => options?.items.find((g) => g.id === id)?.members;
  const invalid = new Set(count?.invalid ?? []);

  return (
    <div>
      <div style={{ ...s.to, ...(disabled ? { background: C.page } : {}) }}>
        {fields.groupIds.map((id) => (
          <span key={id} style={{ ...s.chip, ...s.groupChip }}>
            <UsersIcon />
            {groupName(id)}
            {groupMembers(id) !== undefined ? <span style={{ opacity: 0.75, fontWeight: 500 }}>{groupMembers(id)}</span> : null}
            {disabled ? null : (
              <ChipRemove label={`Remove ${groupName(id)}`} onClick={() => onChange({ groupIds: fields.groupIds.filter((g) => g !== id) })} />
            )}
          </span>
        ))}
        {fields.addresses.map((a) => (
          <span
            key={a}
            title={invalid.has(a) ? 'This is not an email address, so it will be skipped' : undefined}
            style={{ ...s.chip, ...(invalid.has(a) ? { background: C.dangerBg, color: C.dangerText, borderColor: 'transparent' } : {}) }}
          >
            {a}
            {disabled ? null : <ChipRemove label={`Remove ${a}`} onClick={() => onChange({ addresses: fields.addresses.filter((x) => x !== a) })} />}
          </span>
        ))}
        {disabled ? null : (
          <input
            id="ge-to"
            type="text"
            value={draft}
            placeholder={fields.addresses.length || fields.groupIds.length ? 'Add more addresses' : 'Type addresses, or paste a list'}
            onChange={(e) => {
              const v = e.target.value;
              if (/[,;\s]$/.test(v) && v.trim()) commit(v);
              else setDraft(v);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit(draft);
              } else if (e.key === 'Backspace' && !draft && fields.addresses.length) {
                onChange({ addresses: fields.addresses.slice(0, -1) });
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              commit(draft + ' ' + e.clipboardData.getData('text'));
            }}
            onBlur={() => draft.trim() && commit(draft)}
            style={s.toInput}
          />
        )}
      </div>

      {disabled ? null : (
        <div style={{ position: 'relative', marginTop: '.5rem' }}>
          <Button onClick={() => setPicking((p) => !p)}>Add a security group</Button>
          {picking ? (
            <div role="menu" style={s.menu} onMouseLeave={() => setPicking(false)}>
              {options === null ? (
                <div style={s.menuEmpty}>Loading groups…</div>
              ) : options.items.length === 0 ? (
                <div style={s.menuEmpty}>No security groups exist yet.</div>
              ) : (
                options.items.map((g) => {
                  const added = fields.groupIds.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      role="menuitem"
                      disabled={added}
                      className="rw-navhover"
                      style={{ ...s.menuItem, opacity: added ? 0.55 : 1 }}
                      onClick={() => {
                        onChange({ groupIds: [...fields.groupIds, g.id] });
                        setPicking(false);
                      }}
                    >
                      <span>{g.name}</span>
                      <span style={{ color: C.muted, fontSize: '.8rem' }}>
                        {added ? 'added' : `${g.members} member${g.members === 1 ? '' : 's'}`}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          ) : null}
        </div>
      )}

      {countError ? <div style={{ ...s.hint, color: C.dangerText }}>{countError}</div> : null}
      {count ? <Tally count={count} kind={fields.kind} /> : null}
    </div>
  );
}

function Tally({ count, kind }: { count: RecipientCount; kind: GroupEmailKind }) {
  const skipped = kind === 'announcement' ? (count.optedOut ?? 0) : 0;
  const reaches = Math.max(0, count.total - skipped);
  return (
    <div style={s.tally}>
      <span>
        <b style={{ color: C.text }}>{reaches}</b> will receive it
      </span>
      <span>
        {count.fromGroups} from groups, {count.typed} typed
      </span>
      {count.duplicates ? <span>{count.duplicates} duplicate{count.duplicates === 1 ? '' : 's'} counted once</span> : null}
      {count.optedOut ? (
        <span style={{ color: C.warnText }}>
          {count.optedOut} opted out, {kind === 'announcement' ? 'skipped' : 'still sent (notice)'}
        </span>
      ) : null}
      {count.invalid.length ? (
        <span style={{ color: C.dangerText }}>
          {count.invalid.length} not an address, skipped
        </span>
      ) : null}
      {count.missingGroups.length ? <span style={{ color: C.dangerText }}>{count.missingGroups.length} group no longer exists</span> : null}
    </div>
  );
}

function ChipRemove({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} style={s.chipRemove}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
        <path d="M18 6 6 18M6 6l12 12" />
      </svg>
    </button>
  );
}

function UsersIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

// ── The editor ───────────────────────────────────────────────────────

/**
 * A contenteditable with a toolbar over `execCommand`.
 *
 * `execCommand` is deprecated in name and supported everywhere, and it is the
 * only API that gives undo, paste and selection handling for free. What it
 * produces is reduced to auth's allowlist on save, so whatever a browser emits
 * (a `<font color>`, an `align` attribute) is normalised there, not here.
 *
 * The content is set ONCE, on mount, and read back on every input. Setting it
 * from React state would move the caret to the start on every keystroke.
 */
function RichEditor({
  initialHtml,
  disabled,
  mergeFields,
  onChange,
}: {
  initialHtml: string;
  disabled: boolean;
  mergeFields: { token: string; description: string }[];
  onChange: (html: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const saved = useRef<Range | null>(null);
  const [asking, setAsking] = useState<null | 'link' | 'image' | 'button'>(null);
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    adoptHtml(initialHtml || '<p><br></p>', el);
    // Mount only: see the note above.
  }, []);

  const emit = () => ref.current && onChange(ref.current.innerHTML);

  const remember = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current?.contains(sel.anchorNode)) saved.current = sel.getRangeAt(0).cloneRange();
  };
  const restore = () => {
    ref.current?.focus();
    const sel = window.getSelection();
    if (saved.current && sel) {
      sel.removeAllRanges();
      sel.addRange(saved.current);
    }
  };

  const exec = (command: string, value?: string) => {
    restore();
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(command, false, value);
    remember();
    emit();
  };

  const tool = (label: string, icon: ReactNode, command: string, value?: string) => (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onMouseDown={(e) => {
        e.preventDefault();
        exec(command, value);
      }}
      style={s.tool}
      className="rw-navhover"
    >
      {icon}
    </button>
  );

  const ask = (what: 'link' | 'image' | 'button') => {
    remember();
    setUrl('');
    setText(window.getSelection()?.toString() ?? '');
    setAsking(what);
  };

  const insert = () => {
    const href = url.trim();
    if (!/^(https?:\/\/|mailto:)/i.test(href)) return;
    if (asking === 'link') {
      if (window.getSelection()?.toString() || (saved.current && !saved.current.collapsed)) exec('createLink', href);
      else exec('insertHTML', `<a href="${escapeAttr(href)}">${escapeText(text.trim() || href)}</a>`);
    } else if (asking === 'image') {
      exec('insertHTML', `<img src="${escapeAttr(href)}" alt="${escapeAttr(text.trim())}">`);
    } else if (asking === 'button') {
      exec('insertHTML', `<p><a data-button="1" href="${escapeAttr(href)}">${escapeText(text.trim() || 'Open')}</a></p><p><br></p>`);
    }
    setAsking(null);
  };

  return (
    <div className="rw-editor-frame" style={{ ...s.editorFrame, ...(disabled ? { background: C.page } : {}) }}>
      {disabled ? null : (
        <div role="toolbar" aria-label="Formatting" style={s.toolbar}>
          <select
            aria-label="Text style"
            defaultValue="p"
            onMouseDown={remember}
            onChange={(e) => {
              exec('formatBlock', e.target.value);
              e.target.value = 'p';
            }}
            style={s.toolSelect}
          >
            <option value="p">Paragraph</option>
            <option value="h2">Heading</option>
            <option value="h3">Subheading</option>
            <option value="blockquote">Quote</option>
          </select>
          <Sep />
          {tool('Bold', <Icon d="M6 4h8a4 4 0 0 1 0 8H6zM6 12h9a4 4 0 0 1 0 8H6z" bold />, 'bold')}
          {tool('Italic', <Icon d="M19 4h-9M14 20H5M15 4 9 20" />, 'italic')}
          {tool('Underline', <Icon d="M6 4v6a6 6 0 0 0 12 0V4M4 20h16" />, 'underline')}
          {tool('Strikethrough', <Icon d="M16 4H9a3 3 0 0 0-2.83 4M14 12a4 4 0 0 1 0 8H6M4 12h16" />, 'strikeThrough')}
          <ColorTool disabled={disabled} onPick={(c) => exec('foreColor', c)} onOpen={remember} />
          <Sep />
          {tool('Bulleted list', <Icon d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" />, 'insertUnorderedList')}
          {tool('Numbered list', <Icon d="M10 6h11M10 12h11M10 18h11M4 6h1v4M4 10h2M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />, 'insertOrderedList')}
          {tool('Align left', <Icon d="M21 6H3M15 12H3M17 18H3" />, 'justifyLeft')}
          {tool('Center', <Icon d="M21 6H3M17 12H7M19 18H5" />, 'justifyCenter')}
          <Sep />
          <ToolButton label="Link" onClick={() => ask('link')} disabled={disabled}>
            <Icon d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </ToolButton>
          <ToolButton label="Image" onClick={() => ask('image')} disabled={disabled}>
            <Icon d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM21 15l-5-5L5 21M9 9h.01" />
          </ToolButton>
          <ToolButton label="Button" onClick={() => ask('button')} disabled={disabled}>
            <Icon d="M6 7h12a3 3 0 0 1 3 3v4a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-4a3 3 0 0 1 3-3zM8 12h8" />
          </ToolButton>
          {tool('Divider', <Icon d="M3 12h18" />, 'insertHorizontalRule')}
          <Sep />
          {tool('Clear formatting', <Icon d="M4 7V4h16v3M9 20h6M12 4v16M3 21 21 3" />, 'removeFormat')}
          {tool('Undo', <Icon d="M9 14 4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />, 'undo')}
          {tool('Redo', <Icon d="m15 14 5-5-5-5M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />, 'redo')}
          {mergeFields.length ? (
            <>
              <Sep />
              <select
                aria-label="Insert a field"
                value=""
                onMouseDown={remember}
                onChange={(e) => {
                  if (e.target.value) exec('insertText', `{{${e.target.value}}}`);
                }}
                style={s.toolSelect}
              >
                <option value="">Insert field</option>
                {mergeFields.map((f) => (
                  <option key={f.token} value={f.token}>
                    {`{{${f.token}}}`} {f.description}
                  </option>
                ))}
              </select>
            </>
          ) : null}
        </div>
      )}

      {asking ? (
        <form
          style={s.askBar}
          onSubmit={(e) => {
            e.preventDefault();
            insert();
          }}
        >
          <input
            autoFocus
            type="url"
            aria-label={asking === 'image' ? 'Image address' : 'Link address'}
            placeholder={asking === 'image' ? 'https://… image address' : 'https://…'}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            style={{ ...s.input, flex: 2 }}
          />
          <input
            type="text"
            aria-label={asking === 'image' ? 'Description for screen readers' : asking === 'button' ? 'Button label' : 'Link text'}
            placeholder={asking === 'image' ? 'Description' : asking === 'button' ? 'Button label' : 'Text (optional)'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{ ...s.input, flex: 1 }}
          />
          <Button onClick={insert} disabled={!/^(https?:\/\/|mailto:)/i.test(url.trim())}>
            Insert
          </Button>
          <Button onClick={() => setAsking(null)}>Cancel</Button>
        </form>
      ) : null}

      <div
        ref={ref}
        className="rw-editor"
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Message"
        onInput={emit}
        onKeyUp={remember}
        onMouseUp={remember}
        onBlur={() => {
          remember();
          emit();
        }}
        style={s.editor}
      />
      {disabled ? null : (
        <div style={s.editorFoot}>
          Insert a field to fill each person in: <code style={s.code}>{'{{first name}}'}</code> becomes their first name, or
          "there" when we do not know it.
        </div>
      )}
    </div>
  );
}

const COLORS = ['#101626', '#5B6577', '#2B63E8', '#0A7152', '#95611A', '#B42318', '#CB4A2E', '#5B3FD6'];

function ColorTool({ disabled, onPick, onOpen }: { disabled: boolean; onPick: (c: string) => void; onOpen: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative' }}>
      <ToolButton
        label="Text color"
        disabled={disabled}
        onClick={() => {
          onOpen();
          setOpen((o) => !o);
        }}
      >
        <Icon d="M4 20h16M7 16 12 4l5 12M9 11h6" />
      </ToolButton>
      {open ? (
        <span role="menu" style={s.swatches} onMouseLeave={() => setOpen(false)}>
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              role="menuitem"
              aria-label={`Color ${c}`}
              onMouseDown={(e) => {
                e.preventDefault();
                onPick(c);
                setOpen(false);
              }}
              style={{ ...s.swatch, background: c }}
            />
          ))}
        </span>
      ) : null}
    </span>
  );
}

function ToolButton({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      style={s.tool}
      className="rw-navhover"
    >
      {children}
    </button>
  );
}

function Icon({ d, bold }: { d: string; bold?: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={bold ? 2.4 : 2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

function Sep() {
  return <span aria-hidden="true" style={s.sep} />;
}

// ── Scheduling ───────────────────────────────────────────────────────

function ScheduleMenu({
  disabled,
  onSchedule,
  rescheduling,
}: {
  disabled: boolean;
  onSchedule: (at: Date) => void;
  rescheduling: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(() => toLocalInput(nextAt(new Date(), 'tomorrow', 9)));
  const at = value ? new Date(value) : null;
  const valid = at !== null && !Number.isNaN(at.getTime()) && at.getTime() - Date.now() >= 60_000;
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ');
  const quick: [string, Date][] = [
    ['Tomorrow 9:00 AM', nextAt(new Date(), 'tomorrow', 9)],
    ['Monday 9:00 AM', nextAt(new Date(), 1, 9)],
  ];

  return (
    <span style={{ position: 'relative' }}>
      <Button onClick={() => setOpen((o) => !o)} disabled={disabled}>
        {rescheduling ? 'Reschedule' : 'Schedule'}
      </Button>
      {open ? (
        <div role="dialog" aria-label="Send later" className="rw-popover" style={s.popover}>
          <strong style={{ fontSize: '.92rem' }}>Send later</strong>
          <div style={{ display: 'flex', gap: '.35rem', flexWrap: 'wrap' }}>
            {quick.map(([label, date]) => (
              <button key={label} type="button" style={s.quick} onClick={() => setValue(toLocalInput(date))}>
                {label}
              </button>
            ))}
          </div>
          <input type="datetime-local" aria-label="Date and time" value={value} onChange={(e) => setValue(e.target.value)} style={s.input} />
          <div style={s.hint}>
            {zone} time. The people in each group are read again when it goes, so anyone added before then gets it too.
          </div>
          {!valid && value ? <div style={{ ...s.hint, color: C.dangerText }}>Pick a time at least a minute from now.</div> : null}
          <ModalActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <PrimaryButton
              disabled={!valid}
              onClick={() => {
                if (!at) return;
                setOpen(false);
                onSchedule(at);
              }}
            >
              {valid && at ? `Schedule for ${scheduleLabel(at)}` : 'Schedule'}
            </PrimaryButton>
          </ModalActions>
        </div>
      ) : null}
    </span>
  );
}

// ── Sending ──────────────────────────────────────────────────────────

function ConfirmSend({
  count,
  fields,
  busy,
  onCancel,
  onSend,
}: {
  count: RecipientCount;
  fields: GroupEmailFields;
  busy: boolean;
  onCancel: () => void;
  onSend: () => void;
}) {
  const skipped = fields.kind === 'announcement' ? (count.optedOut ?? 0) : 0;
  const reaches = Math.max(0, count.total - skipped);
  const people = `${reaches} ${reaches === 1 ? 'person' : 'people'}`;
  return (
    <Modal
      title={`Send to ${people} now?`}
      onClose={onCancel}
      width={460}
      footer={
        <ModalActions>
          <Button onClick={onCancel}>Cancel</Button>
          <HoldButton tone="brand" onHold={onSend} disabled={busy || reaches === 0} title="Hold for a moment to send">
            {busy ? 'Sending…' : `Hold to send to ${people}`}
          </HoldButton>
        </ModalActions>
      }
    >
      <div style={{ display: 'grid', gap: '.7rem', fontSize: '.9rem', lineHeight: 1.5, color: C.textMid }}>
        <p style={{ margin: 0 }}>
          <b style={{ color: C.text }}>{fields.subject}</b>, in the {fields.template === 'keyodo' ? 'Keyodo' : 'Ridgewill'} template, as{' '}
          {fields.kind === 'announcement' ? 'an announcement' : 'a notice'}.
        </p>
        <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
          <li>
            {count.fromGroups} from security groups, {count.typed} typed
          </li>
          {count.duplicates ? <li>{count.duplicates} named more than once, sent one copy</li> : null}
          {count.optedOut ? (
            <li>{fields.kind === 'announcement' ? `${count.optedOut} opted out and will be skipped` : `${count.optedOut} opted out, and gets this notice anyway`}</li>
          ) : null}
          {count.invalid.length ? <li>{count.invalid.length} typed entries are not addresses and will be skipped</li> : null}
        </ul>
        <p style={{ margin: 0 }}>Once sent it cannot be recalled. Press and hold the button to send.</p>
      </div>
    </Modal>
  );
}

function Outcomes({ email }: { email: GroupEmail }) {
  const o = email.outcomes;
  if (!o) return null;
  return (
    <Panel title="What happened">
      <div style={{ padding: '.9rem 1.2rem', display: 'grid', gap: '.4rem', fontSize: '.88rem', color: C.textMid }}>
        <div>
          Sent by {email.sentBy ?? 'someone'} <When iso={email.sentAt} /> to {email.recipientCount ?? 0}{' '}
          {email.recipientCount === 1 ? 'person' : 'people'}.
        </div>
        <div>{outcomeSummary(o)}.</div>
        <a href="#/notifications/sends" style={{ color: C.brand }}>
          See each copy on the Sends tab, under {email.kind === 'notice' ? 'group-notice' : 'group-announcement'}
        </a>
      </div>
    </Panel>
  );
}

// ── The preview ──────────────────────────────────────────────────────

function Preview({ fields }: { fields: GroupEmailFields }) {
  const [size, setSize] = useState<'desktop' | 'phone'>('desktop');
  const [rendered, setRendered] = useState<{ subject: string; html: string } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const key = useMemo(() => JSON.stringify([fields.template, fields.kind, fields.subject, fields.preheader, fields.bodyHtml]), [fields]);

  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      adminApi
        .previewGroupEmail(fields)
        .then((r) => live && (setRendered(r), setFailed(null)))
        .catch((err) => live && setFailed(message(err)));
    }, 450);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [key]);

  return (
    <Panel
      title="Preview"
      info={
        <InfoBubble label="Whose copy this is">
          Your own copy: the fields are filled with your name, as if you were one of the recipients. Each person's copy
          uses their own. An announcement also gets the unsubscribe footer with the company's postal address under the
          card.
        </InfoBubble>
      }
      action={
        <Segmented<'desktop' | 'phone'>
          label="Preview size"
          value={size}
          onChange={setSize}
          options={[
            { value: 'desktop', label: 'Desktop' },
            { value: 'phone', label: 'Phone' },
          ]}
        />
      }
    >
      <div style={s.stage}>
        <div style={{ ...s.inbox, maxWidth: size === 'phone' ? 375 : 640 }}>
          <span style={{ fontWeight: 650, color: C.text }}>{fields.template === 'keyodo' ? 'Keyodo' : 'Ridgewill'}</span>
          <span style={{ color: C.text }}>{rendered?.subject || fields.subject || 'No subject'}</span>
          <span style={s.inboxPre}>{fields.preheader || 'The first words of the email'}</span>
        </div>
        {failed ? <Note tone="danger">{failed}</Note> : null}
        <div style={{ width: '100%', maxWidth: size === 'phone' ? 375 : 640 }}>
          {rendered ? <EmailFrame html={rendered.html} /> : <div style={s.previewEmpty}>Rendering…</div>}
        </div>
      </div>
    </Panel>
  );
}

/** The rendered email in a shadow root, its inline styles applied through the CSSOM. */
function EmailFrame({ html }: { html: string }) {
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const root = el.shadowRoot ?? el.attachShadow({ mode: 'open' });
    const frame = document.createElement('div');
    adoptHtml(html, frame);
    root.replaceChildren(frame);
    const stop = (e: Event) => {
      if ((e.target as Element | null)?.closest?.('a')) e.preventDefault();
    };
    root.addEventListener('click', stop);
    return () => root.removeEventListener('click', stop);
  }, [html]);
  return <div ref={host} style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}` }} />;
}

/**
 * Move a document's body into `target`, keeping its inline styles.
 *
 * Parsed by DOMParser, whose document is inert, so nothing in it runs or
 * loads. Each `style` attribute is lifted off before the node joins the page,
 * where the CSP would refuse it, and put back through `style.cssText`, which
 * the CSP does not govern. Scripts, style blocks and metadata are dropped.
 */
export function adoptHtml(html: string, target: HTMLElement): void {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script,style,title,meta,link,iframe,object,embed').forEach((n) => n.remove());
  const styled: [HTMLElement, string][] = [];
  doc.body.querySelectorAll<HTMLElement>('[style]').forEach((n) => {
    styled.push([n, n.getAttribute('style') ?? '']);
    n.removeAttribute('style');
  });
  doc.body.querySelectorAll('*').forEach((n) => {
    for (const a of [...n.attributes]) if (/^on/i.test(a.name)) n.removeAttribute(a.name);
  });
  const bodyStyle = doc.body.getAttribute('style');
  target.replaceChildren(...[...doc.body.childNodes].map((n) => document.adoptNode(n)));
  for (const [n, css] of styled) n.style.cssText = css;
  if (bodyStyle) target.style.cssText = bodyStyle;
}

function escapeAttr(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
function escapeText(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const s = {
  back: { fontSize: '.85rem', color: C.muted, textDecoration: 'none', justifySelf: 'start' as const },
  sub: { fontSize: '.8rem', color: C.muted, marginTop: '.1rem' },
  subjectLink: { color: C.text, textDecoration: 'none', fontWeight: 550 },
  inlineLink: {
    background: 'none',
    border: 0,
    padding: 0,
    font: 'inherit',
    color: C.brand,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  panel: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    minWidth: 0,
  },
  panelHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    padding: '.9rem 1.2rem',
    borderBottom: `1px solid ${C.border}`,
  },
  panelTitle: { margin: 0, fontSize: '.95rem', fontWeight: 640, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  rowLabel: { fontSize: '.83rem', fontWeight: 600, color: C.textMid, paddingTop: '.45rem' },
  hint: { fontSize: '.8rem', color: C.muted, marginTop: '.45rem', lineHeight: 1.45 },
  input: { ...inputStyle, width: '100%', boxSizing: 'border-box' as const },
  segment: { display: 'inline-flex', border: `1px solid ${C.inputBorder}`, borderRadius: 7, overflow: 'hidden', flexWrap: 'wrap' as const },
  segmentButton: {
    border: 0,
    background: C.surface,
    color: C.textMid2,
    padding: '.42rem .8rem',
    fontSize: '.85rem',
    fontWeight: 550,
    fontFamily: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '.45rem',
  },
  to: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '.35rem',
    alignItems: 'center',
    border: `1px solid ${C.inputBorder}`,
    borderRadius: 6,
    padding: '.3rem',
    minHeight: 40,
    background: C.surface,
  },
  toInput: {
    border: 0,
    flex: 1,
    minWidth: 160,
    padding: '.25rem .35rem',
    font: 'inherit',
    fontSize: '.9rem',
    outline: 'none',
    background: 'none',
    color: C.text,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '.35rem',
    padding: '.2rem .25rem .2rem .55rem',
    borderRadius: 6,
    fontSize: '.84rem',
    background: C.page,
    border: `1px solid ${C.border}`,
    color: C.text,
    maxWidth: '100%',
    overflowWrap: 'anywhere' as const,
  },
  groupChip: { background: C.blueTint, borderColor: 'transparent', color: C.blueText, fontWeight: 600 },
  chipRemove: {
    border: 0,
    background: 'none',
    cursor: 'pointer',
    color: 'inherit',
    opacity: 0.65,
    width: 18,
    height: 18,
    display: 'grid',
    placeItems: 'center',
    borderRadius: 4,
    padding: 0,
  },
  menu: {
    position: 'absolute' as const,
    zIndex: 5,
    top: 'calc(100% + 4px)',
    left: 0,
    width: 300,
    maxWidth: 'calc(100vw - 48px)',
    maxHeight: 320,
    overflowY: 'auto' as const,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 9,
    boxShadow: 'var(--rw-admin-shadow-sm, 0 8px 24px rgba(16,22,38,.12))',
    padding: '.3rem',
  },
  menuItem: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    gap: '.75rem',
    border: 0,
    background: 'none',
    textAlign: 'left' as const,
    padding: '.5rem .6rem',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '.88rem',
    fontFamily: 'inherit',
    color: C.text,
  },
  menuEmpty: { padding: '.6rem', fontSize: '.85rem', color: C.muted },
  tally: { display: 'flex', flexWrap: 'wrap' as const, gap: '.35rem .9rem', marginTop: '.55rem', fontSize: '.82rem', color: C.textMid2 },
  editorFrame: { border: `1px solid ${C.inputBorder}`, borderRadius: 6, overflow: 'hidden', background: C.surface },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '.1rem',
    padding: '.3rem',
    borderBottom: `1px solid ${C.border}`,
    background: C.page,
    alignItems: 'center',
  },
  tool: {
    border: 0,
    background: 'none',
    height: 30,
    minWidth: 30,
    borderRadius: 5,
    cursor: 'pointer',
    color: C.textMid,
    display: 'grid',
    placeItems: 'center',
  },
  toolSelect: {
    border: 0,
    background: 'none',
    height: 30,
    borderRadius: 5,
    cursor: 'pointer',
    color: C.textMid,
    fontSize: '.83rem',
    fontFamily: 'inherit',
    padding: '0 .3rem',
    maxWidth: 150,
  },
  sep: { width: 1, height: 20, background: C.border, margin: '0 .25rem' },
  swatches: {
    position: 'absolute' as const,
    zIndex: 5,
    top: 'calc(100% + 4px)',
    left: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 22px)',
    gap: '.35rem',
    padding: '.5rem',
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    boxShadow: 'var(--rw-admin-shadow-sm, 0 8px 24px rgba(16,22,38,.12))',
  },
  swatch: { width: 22, height: 22, borderRadius: 5, border: `1px solid ${C.inputBorder}`, cursor: 'pointer', padding: 0 },
  askBar: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '.4rem',
    padding: '.5rem',
    borderBottom: `1px solid ${C.border}`,
    background: C.rowHover,
  },
  editor: { minHeight: 260, padding: '.9rem 1rem', fontSize: '.95rem', lineHeight: 1.6, outline: 'none', color: C.textMid },
  editorFoot: { fontSize: '.8rem', color: C.textMid2, padding: '.5rem 1rem', borderTop: `1px solid ${C.border}`, background: C.rowHover },
  code: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4, padding: '0 .3rem', fontSize: '.78rem' },
  /* At the end of the form, in the flow. Pinned to the bottom of the window it
     covered the editor while scrolling (Jon, 2026-11-01). */
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '.5rem',
    flexWrap: 'wrap' as const,
    padding: '.8rem 1.2rem',
    background: C.surface,
    borderTop: `1px solid ${C.border}`,
    borderRadius: '0 0 10px 10px',
  },
  saved: { fontSize: '.8rem', color: C.muted, width: '100%' },
  popover: {
    position: 'absolute' as const,
    zIndex: 6,
    bottom: 'calc(100% + 8px)',
    right: 0,
    width: 330,
    maxWidth: 'calc(100vw - 48px)',
    display: 'grid',
    gap: '.7rem',
    padding: '1rem',
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    boxShadow: 'var(--rw-admin-shadow, 0 12px 40px rgba(16,22,38,.18))',
  },
  quick: {
    border: `1px solid ${C.border}`,
    background: C.page,
    borderRadius: 999,
    padding: '.2rem .6rem',
    fontSize: '.78rem',
    cursor: 'pointer',
    color: C.textMid,
    fontFamily: 'inherit',
  },
  stage: { background: C.page, padding: '1.1rem', display: 'grid', justifyItems: 'center', gap: '.8rem' },
  inbox: {
    width: '100%',
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: '.6rem .85rem',
    fontSize: '.82rem',
    display: 'grid',
    gap: '.1rem',
  },
  inboxPre: { color: C.muted, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' },
  previewEmpty: { padding: '3rem 1rem', textAlign: 'center' as const, color: C.muted, fontSize: '.85rem' },
};
